import { emptyPerformanceTotals as blank, performanceValues, addPerformanceTotals, performanceRatio, finiteMetric } from "./performanceTotals.js";
import { reportEntityFields } from "./reportIdentity.js";
import { campaignChoiceKey } from "../../shared/goalContracts.js";
export const EXPLORER_METRICS = {
  spend: "광고비",
  revenue: "전환 매출",
  roas: "ROAS",
  cpa: "CPA",
  ctr: "CTR"
};
export function metricValue(t, metric) {
  if (metric === "revenue") return t.rev;
  if (metric === "spend") return t.spend;
  return performanceRatio(t, metric);
}
export function explorePerformance(rows = [], source = "all", metric = "roas") {
  const selected = rows.filter(r => source === "all" || (r._media || r.source || "미확인") === source);
  const totals = blank(),
    days = new Map(),
    campaigns = new Map();
  for (const row of selected) {
    // Normalize aliases before both grouping and displaying the account.
    const account = String(reportEntityFields({ ...row, account_id: row.account_id || row.meta_account_id }).account_id);
    const key = campaignChoiceKey({ ...row, account_id: account });
    if (!campaigns.has(key)) campaigns.set(key, {
      ...blank(),
      key,
      name: row.campaign_name || "이름 없는 캠페인",
      source: row._media || row.source || "미확인",
      account_id: account,
      account_name: String(row.account_name || "")
    });
    const day = String(row.date || "").slice(0, 10);
    if (day && !days.has(day)) days.set(day, {
      ...blank(),
      date: day
    });
    const values = performanceValues(row);
    for (const t of [totals, campaigns.get(key), ...(day ? [days.get(day)] : [])]) addPerformanceTotals(t, values);
  }
  const ranked = [...campaigns.values()].map(t => ({
    ...t,
    value: metricValue(t, metric)
  })).sort((a, b) => a.value == null ? b.value == null ? 0 : 1 : b.value == null ? -1 : metric === "cpa" ? a.value - b.value : b.value - a.value);
  return {
    totals,
    count: selected.length,
    ranked,
    daily: [...days.values()].sort((a, b) => a.date.localeCompare(b.date)).map(t => ({
      ...t,
      value: metricValue(t, metric)
    }))
  };
}
// Small-sample rule (plan D17, adopted as OD-12①; metrics confirmed 10-21, BUNDLE-3 '답 3' ①; CTR
// test changed 10-23). It only decides a screen badge; ranking, values, reports and PPT never use it.
// ROAS and CPA rest on conversions, so both tests apply: fewer than 5 conversions, or under 1% of
// the spend in view. CTR rests on clicks the same way, so it has one test: fewer than 5 clicks. The
// spend share does not apply to CTR: a campaign with tens of thousands of impressions can still
// spend under 1%. Spend and revenue are amounts, so any other metric gets no badge.
// totalSpend is the spend of the media and period in view (smallSampleSpendBase); the CTR test does
// not use it. Missing values decide nothing.
export const SMALL_SAMPLE_MIN_CV = 5;
export const SMALL_SAMPLE_MIN_SPEND_PCT = 1;
export const SMALL_SAMPLE_MIN_CLICK = 5;
export const SMALL_SAMPLE_CV_METRICS = ["roas", "cpa"];
export function isSmallSample(row, totalSpend, metric) {
  if (metric === "ctr") {
    const click = finiteMetric(row?.click);
    return click !== null && click < SMALL_SAMPLE_MIN_CLICK;
  }
  if (!SMALL_SAMPLE_CV_METRICS.includes(metric)) return false;
  const cv = finiteMetric(row?.cv);
  if (cv !== null && cv < SMALL_SAMPLE_MIN_CV) return true;
  const spend = finiteMetric(row?.spend),
    total = finiteMetric(totalSpend);
  return spend !== null && total !== null && total > 0 && spend * 100 < total * SMALL_SAMPLE_MIN_SPEND_PCT;
}
// The share denominator: the card's spend total. When that total shows '—' because some spend is
// missing, the campaigns whose spend is known stand in: a campaign with any missing spend is left
// out, and refunds (negative spend) stay in. What is left out is cost and never negative in total
// (brand-search contract daily spend >= 0), so this sum is a lower bound of the real total. A share
// under 1% of it is under 1% of the real total too; a share of 1% or more of it decides nothing.
// It only compares, so a missing value is never shown or counted as 0.
export function smallSampleSpendBase({ totals, ranked } = {}) {
  const total = finiteMetric(totals?.spend);
  if (total !== null) return total;
  return (ranked || []).reduce((sum, campaign) => sum + (finiteMetric(campaign?.spend) ?? 0), 0);
}
export function explorerFormat(value, metric) {
  if (value == null) return "—";
  return ["roas", "ctr"].includes(metric) ? `${value.toLocaleString("ko-KR", {
    maximumFractionDigits: 1
  })}%` : `₩${Math.round(value).toLocaleString("ko-KR")}`;
}
